import type { Dispatch, PropsWithChildren, SetStateAction } from 'react'
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'

export type WizardModes = 'single' | 'multiple' | 'finish'

export interface WizardContextValues {
  isSingleMode: boolean
  isMultipleMode: boolean
  isLastStep: boolean
  controlMode: WizardModes
  currentStep: number
  totalSteps: number
  isNextDisabled: boolean
  isPrevDisabled: boolean
  canGoToNextStep: boolean
  canGoToPrevStep: boolean
  goToNextStep: () => void
  goToPrevStep: () => void
  toggleNext: () => void
  togglePrev: () => void
  setIsNextDisabled: Dispatch<SetStateAction<boolean>>
  setIsPrevDisabled: Dispatch<SetStateAction<boolean>>
  setStep: Dispatch<SetStateAction<number>>
  setControlMode: Dispatch<SetStateAction<WizardModes>>
  reset: () => void
  isCurrentStep: (step: number) => boolean
}

export interface WizardProviderProps {
  initialStep?: number
  totalSteps?: number
  disableNext?: boolean
  disablePrev?: boolean
}

export const WizardContext = createContext<WizardContextValues | null>(null)

export const WizardProvider = ({
  children,
  initialStep = 0,
  totalSteps = 1,
  disableNext = false,
  disablePrev = false
}: PropsWithChildren<WizardProviderProps>) => {
  const [controlMode, setControlMode] = useState<WizardModes>('single')
  const [currentStep, setCurrentStep] = useState<number>(initialStep)
  const [isPrevDisabled, setIsPrevDisabled] = useState<boolean>(disablePrev)
  const [isNextDisabled, setIsNextDisabled] = useState<boolean>(disableNext)

  const canGoToNextStep = useMemo(
    () => currentStep + 1 < totalSteps,
    [totalSteps, currentStep]
  )

  const canGoToPrevStep = useMemo(() => currentStep - 1 >= 0, [currentStep])

  const setStep = useCallback(
    (step: number | Function) => {
      const newStep = step instanceof Function ? step(currentStep) : step

      if (newStep >= 0 && newStep <= totalSteps) setCurrentStep(newStep)
    },
    [totalSteps, currentStep]
  )

  const goToNextStep = useCallback(() => {
    if (canGoToNextStep) setCurrentStep(step => step + 1)
  }, [canGoToNextStep])

  const goToPrevStep = useCallback(() => {
    if (canGoToPrevStep) setCurrentStep(step => step - 1)
  }, [canGoToPrevStep])

  const reset = useCallback(() => {
    setCurrentStep(initialStep)
  }, [initialStep])

  const togglePrev = useCallback(() => {
    setIsPrevDisabled(prev => !prev)
  }, [])

  const toggleNext = useCallback(() => {
    setIsNextDisabled(prev => !prev)
  }, [])

  const isCurrentStep = useCallback(
    (step: number) => step === currentStep,
    [currentStep]
  )

  return (
    <WizardContext.Provider
      value={{
        isSingleMode: controlMode === 'single',
        isMultipleMode: controlMode === 'multiple',
        isLastStep: controlMode === 'finish',
        isCurrentStep,
        controlMode,
        currentStep,
        totalSteps,
        isNextDisabled,
        isPrevDisabled,
        canGoToNextStep,
        canGoToPrevStep,
        goToNextStep,
        goToPrevStep,
        toggleNext,
        togglePrev,
        setIsNextDisabled,
        setIsPrevDisabled,
        setStep,
        setControlMode,
        reset
      }}
    >
      {children}
    </WizardContext.Provider>
  )
}

export const useWizard = (): WizardContextValues => {
  const context = useContext(WizardContext)

  if (context === null) {
    throw new Error('Attempted to call useWizard outside the Wizard component.')
  }

  return context
}
